import React, { useCallback, useMemo, useState } from "react";

import { FormInstance } from "antd/es/form/hooks/useForm";
import Menu from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import FollowUpTime from "@mapmycustomers/shared/enum/activity/FollowUpTime";

import localSettings from "@app/config/LocalSettings";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import isValidDate from "@app/util/isValidDate";

import FormValues from "../../types/FormValues";
import { getFollowUpDate } from "../../utils/getFollowUpDate";
import { getFollowUpTimeDisplayName } from "../../utils/getFollowUpTimeDisplayName";

import styles from "./FollowUpTimeMenu.module.scss";

interface FollowUpTimeMenuProps {
  form: FormInstance<FormValues>;
  onSetCustom: (custom: boolean) => void;
}

const FollowUpTimeMenu: React.FC<FollowUpTimeMenuProps> = ({ form, onSetCustom }) => {
  const intl = useIntl();
  const [selectedKeys, setSelectedKeys] = useState<FollowUpTime[]>([
    localSettings.getFollowUpTime(),
  ]);
  const [previousActivityDate] = useState(() => {
    const startAt = form.getFieldValue(ActivityFieldName.START_AT);
    return getFollowUpDate(startAt, localSettings.getFollowUpTime(), "subtract");
  });

  const handleClick = useCallback(
    (item: MenuInfo) => {
      const followUpTime = item.key as FollowUpTime;
      localSettings.setFollowUpTime(followUpTime);
      setSelectedKeys([followUpTime]);

      if (followUpTime === FollowUpTime.CUSTOM) {
        onSetCustom(true);
        return;
      }

      const updatedStartAt = getFollowUpDate(previousActivityDate, followUpTime);

      form.setFieldValue(ActivityFieldName.START_AT, updatedStartAt);
    },
    [form, onSetCustom, previousActivityDate]
  );

  const items = useMemo(() => {
    return Object.values<FollowUpTime>(FollowUpTime).map((value) => ({
      key: value,
      label: getFollowUpTimeDisplayName(intl, value),
    }));
  }, [intl]);

  if (!isValidDate(previousActivityDate)) {
    return null;
  }

  return (
    <Menu className={styles.menu} items={items} onClick={handleClick} selectedKeys={selectedKeys} />
  );
};

export default FollowUpTimeMenu;
